import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, Observable, throwError } from "rxjs";
import { ErrorService } from "../error/error.service";
import { LoggerToasterService } from "../logger-toaster/logger-toaster.service";

export interface Httpoptions {
    headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
    };
    context?: HttpContext;
    observe?: any; // 'events' | 'body' | 'response'
    params?:
    | HttpParams
    | {
        [param: string]: string | string[];
    };
    reportProgress?: boolean;
    responseType?: any;
    withCredentials?: boolean;
}

export interface HttpRequestoptions extends Httpoptions {
    body?: any;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface CallRoutingoptions<T> {
    httpoptions?: HttpRequestoptions;
    headers?: HttpHeaders;
    showErrorToast?: boolean;
    showErrorConfirmation?: boolean;
    suppressSpinner?: boolean;
    allowstringData?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class ServiceHelper {
    constructor(
        private readonly http: HttpClient,
        private readonly loggerToastr: LoggerToasterService,
        private readonly errorservice: ErrorService,
    ) { }

    get$<T>(url: string, options?: CallRoutingoptions<T>): Observable<T> {
        return this.executeRequest$<T>('get', url, null, options);
    }

    post$<T>(url: string, data: any, options?: CallRoutingoptions<T>): Observable<T> {
        return this.executeRequest$<T>('post', url, data, options);
    }

    put$<T>(url: string, data: any, options?: CallRoutingoptions<T>): Observable<T> {
        return this.executeRequest$<T>('put', url, data, options);
    }

    patch$<T>(url: string, data: any, options?: CallRoutingoptions<T>): Observable<T> {
        return this.executeRequest$<T>('patch', url, data, options);
    }

    delete$<T>(url: string, options?: CallRoutingoptions<T>): Observable<T> {
        return this.executeRequest$<T>('delete', url, null, options);
    }

    executeRequest$<T>(
        method: string,
        url: string,
        data: any,
        routingopts?: CallRoutingoptions<T>,
    ): Observable<T> {
        const withSpinnerOptions = this.setspinneroptions(routingopts);
        const setupDefaultRoutingopts = this.setupDefaultRoutingoptions(withSpinnerOptions);
        const requestoptions = this.createRequestoptions<T>(data, setupDefaultRoutingopts);

        return this.http.request<T>(method, url, requestoptions).pipe(
            catchError((errorResponse: unknown) => {
                this.errorHandling(errorResponse as HttpErrorResponse, setupDefaultRoutingopts);
                return throwError(() => errorResponse);
            })
        );
    }

    private readonly createRequestoptions = <T>(
        data: any,
        routingOpts: CallRoutingoptions<T>,
    ): HttpRequestoptions => {
        const requestoptions: HttpRequestoptions = routingOpts
            ? { ...routingOpts, ...routingOpts.httpoptions }
            : routingOpts

        if (requestoptions.responseType === undefined) {
            requestoptions.responseType = 'json';
        }

        if (data !== null) {
            if (typeof data === 'string' && !routingOpts.allowstringData) {
                requestoptions.body = { value: data };
            } else if (typeof data === 'string') {
                requestoptions.body = JSON.stringify(data);
                requestoptions.headers = new HttpHeaders({ 'Content-Type': 'application/json' });
                if (routingOpts.suppressSpinner) {
                    requestoptions.headers = requestoptions.headers.append('suppressSpinner', 'true');
                }
            } else {
                requestoptions.body = data;
            }
        }

        return requestoptions;
    };

    private readonly setspinneroptions = <T>(routingopts?: CallRoutingoptions<T>): CallRoutingoptions<T> | undefined => {
        if (routingopts && routingopts?.suppressSpinner) {
            routingopts.headers = (routingopts.headers ?? new HttpHeaders()).append('suppressSpinner', 'true');
        }

        return routingopts;
    };

    private readonly setupDefaultRoutingoptions = <T>(
        routingOpts: CallRoutingoptions<T> = { httpoptions: {} },
    ): CallRoutingoptions<T> => {
        if (routingOpts.showErrorToast === undefined) {
            return {
                ...routingOpts,
                showErrorToast: true,
            };
        }

        return routingOpts;
    };

    private errorHandling(errorResponse: HttpErrorResponse, routingOpts: CallRoutingoptions<unknown>): void {
        if (routingOpts.showErrorToast) {
            const messages = this.errorservice.getvalidationserverMessages(errorResponse);

            if (messages?.length) {
                messages.forEach((message: string) => this.loggerToastr?.error?.(message));
            } else {
                const message = this.errorservice.getServerMessage(errorResponse);
                this.loggerToastr?.error?.(message);
            }
        }

        if (routingOpts.showErrorConfirmation !== undefined) {
            const message = this.errorservice.getServerMessage(errorResponse);
            console.warn(message);
        }
    }
}
